// Shared catalog/game types. The slim CatalogItem is what /api/catalog returns
// per card; GameDetail is the full record from /api/catalog/:id.

export type Playability = "verified" | "experimental" | "queued";
export type Shelf = "playable" | "multiplayer" | "flash" | "java" | "lab";
export type SortMode = "az" | "plays";

export interface CatalogItem {
  id: string;
  title: string;
  type: string | null;
  category: string | null;
  source: string | null;
  thumbnail: string | null;
  multiplayer: boolean;
  experimental: boolean;
  playability: Playability;
  playCount: number;
  runtime: string | null;
  catalog: string;
}

export interface CatalogPage {
  items: CatalogItem[];
  nextCursor: string | null;
  total: number;
}

export interface Facet {
  value: string;
  count: number;
}

export interface Facets {
  sources: Facet[];
  categories: Facet[];
}

export interface GameParam {
  name: string;
  value: string;
}

export interface GamePlayer {
  type?: string;
  code?: string;
  appletName?: string;
  archive?: string;
  codebase?: string;
  swf?: string;
  script?: string;
  url?: string;
  params?: GameParam[];
  [key: string]: unknown;
}

export interface GameDetail {
  id: string;
  title: string;
  type: string;
  category?: string;
  description?: string;
  instructions?: string;
  rating?: string;
  playCount?: string | number;
  width?: number;
  height?: number;
  thumbnail?: string;
  sourcePageUrl?: string;
  source?: string;
  multiplayer?: boolean;
  family?: string;
  catalog: string;
  playability: Playability;
  player: GamePlayer;
  // Optional YOG transport hints used by the player.
  yogWs?: string;
  yogPort?: number;
  yogRoom?: string;
  yogPageId?: string;
  yogLdictPath?: string;
  yogRequiredAssets?: string[];
  queueReason?: string;
  [key: string]: unknown;
}

export interface CatalogFilters {
  shelf: Shelf;
  search: string;
  category: string;
  source: string;
  sort: SortMode;
}

export interface ScoreRow {
  rank: number;
  name: string;
  score: number;
  at?: number;
}

export interface HighScores {
  game: string;
  period: string;
  order: string;
  scores: ScoreRow[];
}

export interface ScoreAdapter {
  capture?: string;
  method?: string;
  order?: string;
  [key: string]: unknown;
}

export interface Rating {
  game: string;
  rating?: number;
  wins?: number;
  losses?: number;
  games?: number;
}

// URL-backed catalog search params (TanStack Router validateSearch output).
export interface CatalogSearch {
  shelf: Shelf;
  q: string;
  cat: string;
  src: string;
  sort: SortMode;
}

export const DEFAULT_CATALOG_SEARCH: CatalogSearch = {
  shelf: "playable",
  q: "",
  cat: "",
  src: "",
  sort: "az",
};
